import {
  BOOLEAN_FIELD_ALIASES,
  DIVIDEND_PAYMENT_TYPE_ALIAS,
  INVESTMENT_DATE_ALIAS,
  SECURITY_TYPE_ALIAS,
} from 'common/constants/cap-table/capTable';
import { PERCENT, PERCENTAGE } from 'common/constants/general';
import { DIVIDEND_PAYMENT_CASH, DIVIDEND_PAYMENT_PIK } from 'common/constants/inputs';
import { LIST as SECURITY_TYPE_LIST } from 'common/constants/securityTypes';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { DEFAULT_CURRENCY } from 'components/FeaturedSpreadsheet/constants';
import { finiteNumberOf } from './aiNumericValue';
import { AiDateFields, parseAiDict } from './parseAiDict';

const YES_NO_LABELS = ['No', 'Yes'];

// Prefer earliestGrantDate, then asOfDate. Empty/null fields are skipped.
const pickAiDateDisplay = (fields: AiDateFields | null | undefined): string | undefined =>
  fields?.earliestGrantDate ?? fields?.asOfDate ?? undefined;

/**
 * Investment-date display: plain date strings pass through; Python/JSON dict
 * shapes (and already-parsed objects) extract earliestGrantDate ?? asOfDate.
 * Dict-shaped values with no usable date return '' — never the raw dict /
 * "[object Object]" string that this path exists to hide.
 */
const formatInvestmentDateValue = (val: unknown): string => {
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    return pickAiDateDisplay(val as AiDateFields) ?? '';
  }
  if (typeof val === 'string' && val.trimStart().startsWith('{')) {
    return pickAiDateDisplay(parseAiDict<AiDateFields>(val)) ?? '';
  }
  return String(val);
};

/**
 * Numeric tail of formatDisplayValue: the cell's suffix / percent / currency /
 * locale pipeline. `exactDigits` overrides every path's fraction digits (used
 * by formatDistinctDisplayPair to extend precision until a merge pair visibly
 * differs); without it each path keeps its established default.
 */
const renderNumber = (
  num: number,
  cellFormat?: RowFormat,
  gridType?: string,
  currencyCode?: string,
  exactDigits?: number
): string => {
  if (cellFormat?.suffix) {
    return `${num.toFixed(exactDigits ?? cellFormat.maximumFractionDigits ?? 0)}${cellFormat.suffix}`;
  }
  if (gridType === PERCENTAGE || cellFormat?.style === PERCENT) {
    const displayNum = cellFormat?.isDecimal !== false ? num * 100 : num;
    return `${displayNum.toFixed(exactDigits ?? cellFormat?.maximumFractionDigits ?? 2)}%`;
  }
  if (cellFormat?.style === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode ?? DEFAULT_CURRENCY,
      minimumFractionDigits: exactDigits ?? cellFormat.minimumFractionDigits,
      maximumFractionDigits: exactDigits ?? cellFormat.maximumFractionDigits,
    }).format(num);
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: exactDigits,
    maximumFractionDigits: exactDigits ?? 6,
  });
};

/**
 * Human display for an AI-suggested (or baseline) value, using the CELL's own
 * format config: security-type ids map to their labels, dividend payment type
 * to Cash/PIK, booleans to Yes/No, numbers through the cell's suffix / percent
 * / currency / locale rules. The single formatter shared by the in-grid AI
 * viewers (merge text, empty-cell preview) and the review panel's card
 * headline, so a value always reads the same everywhere.
 *
 * Lives in utilities (not next to the viewers) so reviewAggregator can share
 * it without a component-module cycle (the viewers import reviewStatusOf).
 */
export const formatDisplayValue = (
  val: unknown,
  dbType?: string,
  fieldAlias?: string,
  cellFormat?: RowFormat,
  gridType?: string,
  currencyCode?: string
): string => {
  if (val === null || val === undefined || val === '') {
    return '';
  }
  if (fieldAlias === INVESTMENT_DATE_ALIAS) {
    return formatInvestmentDateValue(val);
  }
  if (fieldAlias === SECURITY_TYPE_ALIAS) {
    const label = SECURITY_TYPE_LIST[Number(val) - 1];
    return label ?? String(val);
  }
  if (fieldAlias === DIVIDEND_PAYMENT_TYPE_ALIAS) {
    const normalized = typeof val === 'string' ? val.toLowerCase() : val;
    if (normalized === 0 || normalized === DIVIDEND_PAYMENT_CASH.toLowerCase()) {
      return DIVIDEND_PAYMENT_CASH;
    }
    if (normalized === 1 || normalized === DIVIDEND_PAYMENT_PIK.toLowerCase()) {
      return DIVIDEND_PAYMENT_PIK;
    }
  }
  if (dbType === 'boolean' || BOOLEAN_FIELD_ALIASES.has(fieldAlias ?? '')) {
    let idx: number;
    if (typeof val === 'boolean') {
      idx = val ? 1 : 0;
    } else {
      idx = Number(val);
    }
    return YES_NO_LABELS[idx] ?? String(val);
  }
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  // Lenient parse (finiteNumberOf): raw doc tokens like "$133.5" must flow
  // through the cell's own number pipeline (2dp for currency cells) instead of
  // rendering verbatim with document precision (SCAL-9263).
  const num = finiteNumberOf(val);
  if (num !== undefined) {
    return renderNumber(num, cellFormat, gridType, currencyCode);
  }
  return String(val);
};

/**
 * Displays for a merge pair (baseline -> suggested) whose REAL numeric
 * difference can be smaller than the cell's display precision: when both sides
 * format identically yet differ in double space, re-render BOTH sides with the
 * cell's own pipeline at extended precision - one fraction digit past the
 * first divergence, capped at 15 - so the arrow never reads "same -> same"
 * (a 2dp "52.55x -> 52.55x" becomes "52.547078x -> 52.547108x"). Pairs that
 * already differ, non-numeric values, and equal doubles come back unchanged.
 */
export const formatDistinctDisplayPair = (
  baseline: unknown,
  suggested: unknown,
  dbType?: string,
  fieldAlias?: string,
  cellFormat?: RowFormat,
  gridType?: string,
  currencyCode?: string
): [string, string] => {
  const pair: [string, string] = [
    formatDisplayValue(baseline, dbType, fieldAlias, cellFormat, gridType, currencyCode),
    formatDisplayValue(suggested, dbType, fieldAlias, cellFormat, gridType, currencyCode),
  ];
  const numBaseline = finiteNumberOf(baseline);
  const numSuggested = finiteNumberOf(suggested);
  if (pair[0] !== pair[1] || numBaseline === undefined || numSuggested === undefined || numBaseline === numSuggested) {
    return pair;
  }
  const at = (digits: number): [string, string] => [
    renderNumber(numBaseline, cellFormat, gridType, currencyCode, digits),
    renderNumber(numSuggested, cellFormat, gridType, currencyCode, digits),
  ];
  // The first differing digit alone is often a rounding artifact (52.54708 vs
  // 52.54711 - neither spelling holds those digits), so show one digit PAST the
  // last still-equal precision: the true tails (52.547078 vs 52.547108).
  let lastEqual = -1;
  for (let digits = 0; digits <= 15; digits += 1) {
    const [a, b] = at(digits);
    if (a === b) {
      lastEqual = digits;
    }
  }
  if (lastEqual === 15) {
    return pair; // indistinguishable within the column's precision
  }
  return at(Math.min(lastEqual + 2, 15));
};

export default formatDisplayValue;
